// components/modals/LocationInfoModal.js
import React, { useMemo, useState, useEffect } from 'react';
import { Divider, Modal, Table, Input } from 'antd';

const { Search } = Input;

const LocationInfoModal = ({
  visible,
  onCancel,
  locationDetails
}) => {
  const [tableHeight, setTableHeight] = useState(400);
  const [searchText, setSearchText] = useState("");

  // Auto-adjust table height on window resize
  useEffect(() => {
    const handleResize = () => {
      const availableHeight = window.innerHeight - 250;
      setTableHeight(Math.max(200, availableHeight));
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Aisle filters
  const aisleFilters = useMemo(() => {
    if (!locationDetails?.length) return [];
    const map = new Map();
    for (const item of locationDetails) {
      const id = item?.aisle_id;
      if (!map.has(id)) map.set(id, item?.aisle ?? String(id));
    }
    return Array.from(map, ([value, text]) => ({ text, value }));
  }, [locationDetails]);

  // Apply search filtering first
  const filteredData = useMemo(() => {
    if (!locationDetails?.length) return [];
    if (!searchText) return locationDetails;

    const lower = searchText.toLowerCase();
    return locationDetails.filter(item => {
      return (
        String(item?.aisle ?? "").toLowerCase().includes(lower) ||
        String(item?.code ?? "").toLowerCase().includes(lower)
      );
    });
  }, [locationDetails, searchText]);

  // Rowspan & grouping logic (unchanged, just uses filteredData)
  const dataWithRowSpan = useMemo(() => {
    if (!filteredData?.length) return [];

    const rows = filteredData
      .map(r => ({ ...r }))
      .sort((a, b) => {
        const aId = Number(a?.aisle_id);
        const bId = Number(b?.aisle_id);
        if (aId !== bId) return aId - bId;
        return String(a?.code ?? '').localeCompare(String(b?.code ?? ''), undefined, { numeric: true, sensitivity: 'base' });
      });

    const out = [];
    let i = 0;
    let groupIndex = 0;
    while (i < rows.length) {
      const aisleId = rows[i].aisle_id;
      let j = i;
      let totalRemaining = 0;
      while (j < rows.length && rows[j].aisle_id === aisleId) {
        totalRemaining += Number(rows[j]?.remaining_capacity ?? 0);
        j++;
      }
      const groupLen = j - i;

      for (let k = i; k < j; k++) {
        out.push({
          ...rows[k],
          _rowSpan: k === i ? groupLen : 0,
          _isGroupStart: k === i,
          _groupIndex: groupIndex,
          _groupTotalRemaining: k === i ? totalRemaining : undefined
        });
      }
      groupIndex++;
      i = j;
    }
    return out;
  }, [filteredData]);

  const columns = [
    {
      title: 'Aisle',
      dataIndex: 'aisle',
      key: 'aisle',
      width: 100,
      filters: aisleFilters,
      onFilter: (value, record) => String(record.aisle_id) === String(value),
      onCell: (record) => ({ rowSpan: record._rowSpan })
    },
    {
      title: 'Total Remaining Capacity',
      align: 'center',
      width: 180,
      render: (record) => {
        if (!record._isGroupStart) return null;
        const total = Number(record._groupTotalRemaining ?? 0);
        return <span style={{ color: 'blue', fontWeight: 'bold' }}>{total.toFixed(2)}</span>;
      },
      onCell: (record) => ({ rowSpan: record._rowSpan })
    },
    {
      title: 'Location',
      width: 120,
      dataIndex: 'code',
      render: (code) => <>{code}</>
    },
    {
      title: 'Pallet Capacity',
      align: 'center',
      width: 140,
      render: (record) => <>{Number(record?.pallet_capacity ?? 0).toLocaleString()}</>
    },
    {
      title: 'Used Capacity',
      align: 'center',
      width: 140,
      render: (record) => {
        const palletCap = Number(record?.pallet_capacity ?? 0);
        const remainingCap = Number(record?.remaining_capacity ?? 0);
        const used = Math.max(0, palletCap - remainingCap);
        return <span style={{ color: 'red', fontWeight: 'bold' }}>{used.toFixed(2)}</span>;
      }
    },
    {
      title: 'Remaining Capacity',
      align: 'center',
      width: 160,
      render: (record) => {
        const remainingCap = Number(record?.remaining_capacity ?? 0);
        return <span style={{ color: 'green', fontWeight: 'bold' }}>{remainingCap.toFixed(2)}</span>;
      }
    }
  ];

  return (
    <Modal
      width="80vw"
      title="Locations Details"
      open={visible}
      onCancel={onCancel}
      cancelText="Close"
      okButtonProps={{ style: { display: 'none' } }}
      style={{ top: 20 }}
    >
      <Divider style={{ margin: '2px 2px' }} />

      {/* 🔍 Search box */}
      <Search
        placeholder="Search by aisle or location"
        allowClear
        type='primary'
        onSearch={setSearchText}
        onChange={(e) => setSearchText(e.target.value)}
        style={{ marginBottom: 10, width: 300 }}
      />

      <Table
        dataSource={dataWithRowSpan}
        columns={columns}
        pagination={false}
        scroll={{ x: 800, y: tableHeight }}
        bordered
        rowKey={(r) => r.location_id ?? r.id ?? r.code ?? `${r.aisle_id}-${r.code}`}
        rowClassName={(record) =>
          record._isGroupStart && record._groupIndex % 2 === 1 ? 'table-row-striped' : ''
        }
      />
    </Modal>
  );
};

export default LocationInfoModal;
